/**
 * @friskai/frisk-js Session
 *
 * FriskSession represents a single session for tracking tool calls and tracing.
 */

import { context, propagation, type Tracer, trace } from '@opentelemetry/api'
import { v4 as uuidv4 } from 'uuid'
import { ToolCallEvaluationError } from '../errors'
import { captureError } from '../errors/capture-error'
import { FriskSDKLoggingOptions, SDKLogger } from '../logging'
import { deriveSdkLogger } from '../logging/derive-sdk-logger'
import type { DecideToolCallOptions } from './extensions'
import type { Frisk } from './frisk'
import { extractLlmReasoning } from './llm-reasoning/extractLlmReasoning'
import { removeLlmReasoningArg } from './llm-reasoning/removeLlmReasoningArg'
import { type ResolvedRedactionOptions } from './redaction-options'
import { SessionRegistry } from './session-registry'
import {
  FriskSpan,
  LlmCallSpan,
  OperationSpan,
  SessionSpan,
  ToolCallDecisionSpan,
  ToolCallObservationSpan,
} from './spans'
import { DecisionOutcome, ToolCallDecision, TraceContextCarrier } from './types'

type AnyFrisk = Frisk<any, any, any, any>

export interface FriskSessionOptions<FriskClass extends AnyFrisk = AnyFrisk> {
  frisk: FriskClass
  redact: ResolvedRedactionOptions
  tracer: Tracer
  logging?: FriskSDKLoggingOptions
}

interface InitTracingOptions {
  runId: string
  agentState: Record<string, unknown>
}

export enum ToolCallStatus {
  Success = 'success',
  Error = 'error',
}

/**
 * A Frisk session for tracking a sequence of tool calls
 *
 * Sessions provide:
 * - Tool call evaluation against policies
 * - Tracing context for distributed tracing
 * - State management for the session lifecycle
 */
export class FriskSession<TFrisk extends AnyFrisk = AnyFrisk> {
  public readonly frisk: TFrisk
  public readonly id: string

  private _rootSpan: SessionSpan | null = null
  protected redaction: ResolvedRedactionOptions
  protected tracer: Tracer
  protected logger: SDKLogger
  protected _rootRunId: string | null = null
  protected _isTracing: boolean = false
  protected _agentState: Record<string, unknown> | null = null

  private toolCallSequenceNumber: number = 0
  private toolCallSequenceGenerator = this.createToolCallSequenceGenerator()
  private toolCallErrorCount: number = 0

  // Cumulative token usage summed across every LLM call recorded in this
  // session, mirrored onto the root SessionSpan as it accrues.
  private tokenUsageInput: number = 0
  private tokenUsageOutput: number = 0
  private tokenUsageTotal: number = 0

  constructor({ frisk, redact, tracer, logging }: FriskSessionOptions<TFrisk>) {
    const sessionId = (
      this.constructor as typeof FriskSession
    ).generateSessionId()

    this.logger = deriveSdkLogger(logging, {
      component: 'FriskSession',
    })
    this.frisk = frisk
    this.id = sessionId
    this.redaction = redact
    this.tracer = tracer
  }

  public get rootSpan(): SessionSpan | null {
    return this._rootSpan
  }

  public getTracer(): Tracer {
    return this.tracer
  }

  /**
   * Check if tracing is active
   */
  get isTracing(): boolean {
    return this._isTracing
  }

  get rootRunId(): string | null {
    return this._rootRunId
  }

  /**
   * Open a span for a single LLM invocation, parented under `parent` (or the
   * session root span).
   */
  createLlmCallSpan({
    parent,
  }: {
    parent?: FriskSpan | null
  } = {}): LlmCallSpan {
    const span = new LlmCallSpan({
      tracer: this.tracer,
      parent: parent ?? this._rootSpan,
    })
    span.setAttributes({ sessionId: this.id })
    return span
  }

  /**
   * Open a span for a single operation (e.g. a nested LangChain run), parented
   * under `parent` (or the session root span).
   */
  createOperationSpan({
    parent,
  }: {
    parent?: FriskSpan | null
  } = {}): OperationSpan {
    const span = new OperationSpan({
      tracer: this.tracer,
      parent: parent ?? this._rootSpan,
    })
    span.setAttributes({ sessionId: this.id })
    return span
  }

  async createToolCallSpan({
    name,
    args: toolArgsInput,
    agentState,
    parent,
  }: {
    name: string
    args: object | any
    agentState?: Record<string, unknown> | null
    parent?: FriskSpan | null
  }): Promise<ToolCallObservationSpan> {
    const toolArgs = this.validateToolArgs(toolArgsInput)
    const llmReasoning = extractLlmReasoning(toolArgs)
    const redactedToolArgsResult = this.frisk.adapter?.serializeToolArgs(
      removeLlmReasoningArg(toolArgs),
      { redact: this.redaction.redactToolArgs },
    ) ?? {
      value: '{}',
      redactedPaths: [],
    }
    const agentStateToUse = agentState ?? this._agentState ?? {}
    const redactedAgentStateResult = this.frisk.adapter?.serializeAgentState(
      agentStateToUse,
      { redact: this.redaction.redactAgentState },
    ) ?? {
      value: '{}',
      redactedPaths: [],
    }

    const span = new ToolCallObservationSpan({
      tracer: this.tracer,
      parent: parent ?? this._rootSpan,
    })
    span.setAttributes({
      sessionId: this.id,
      toolName: name,
      toolArgsJson: (redactedToolArgsResult.value as string) ?? '{}',
      toolArgsRedactedPathsJson: JSON.stringify(
        redactedToolArgsResult.redactedPaths,
      ),
      agentStateJson: (redactedAgentStateResult.value as string) ?? '{}',
      agentStateRedactedPathsJson: JSON.stringify(
        redactedAgentStateResult.redactedPaths,
      ),
      toolCallSequenceNumber: this.toolCallSequenceGenerator.next()
        .value as number,
    })

    await this.frisk.toolRegistrationComplete
    const registeredTool = this.frisk.getRegisteredTool(name)
    if (registeredTool) {
      span.setAttributes({
        friskToolId: registeredTool.id,
        friskToolVersionId: registeredTool.versionId,
      })
    }
    if (llmReasoning !== null) {
      span.setAttributes({ llmReasoning })
    }

    return span
  }

  public closeToolCallSpan({
    toolCallId,
    span,
    err,
    status,
  }: {
    toolCallId: string | null
    span: ToolCallObservationSpan
    status: ToolCallStatus | null
    err: Error | null
  }) {
    if (toolCallId) {
      span.setAttributes({ toolCallId })
    }

    if (status === ToolCallStatus.Error || !!err) {
      this.incrementToolCallErrors()
      span.setAttributes({ isError: true, isSuccess: false })
    } else if (status === ToolCallStatus.Success) {
      span.setAttributes({ isSuccess: true, isError: false })
    }

    if (err instanceof Error) {
      span.setAttributes({
        errorMessage: `${err}`,
        errorType: `${err.constructor.name}`,
      })
    }

    span.end()
  }

  private validateToolArgs(parsedToolArgs: unknown) {
    const isPlainObject =
      typeof parsedToolArgs === 'object' &&
      parsedToolArgs !== null &&
      !Array.isArray(parsedToolArgs) &&
      (Object.getPrototypeOf(parsedToolArgs) === Object.prototype ||
        Object.getPrototypeOf(parsedToolArgs) === null)

    const toolArgs: Record<string, unknown> = isPlainObject
      ? (parsedToolArgs as Record<string, unknown>)
      : {}
    return toolArgs
  }

  /**
   * Evaluate a tool call against policies
   *
   * This method wraps the tool call evaluation in an OpenTelemetry span,
   * handling redaction of sensitive data, latency recording, and trace
   * context propagation. Serialization/redaction and decision/error
   * translation live here; the span is a pure typed-attribute wrapper.
   */
  decideToolCall({
    toolCall,
    agentState,
  }: DecideToolCallOptions): ToolCallDecision {
    const registeredTool = this.frisk.getRegisteredTool(toolCall.name)

    const redactedToolArgs = this.frisk.adapter.serializeToolArgs(
      removeLlmReasoningArg(toolCall.args),
      { redact: this.redaction.redactToolArgs },
    )
    const redactedAgentState = this.frisk.adapter.serializeAgentState(
      agentState,
      { redact: this.redaction.redactAgentState },
    )
    const llmReasoning = extractLlmReasoning(toolCall.args)

    const span = new ToolCallDecisionSpan({
      tracer: this.tracer,
      parent: this._rootSpan,
    })
    try {
      span.setAttributes({
        sessionId: this.id,
        toolName: toolCall.name,
        toolCallId: toolCall.id,
        toolArgsJson: (redactedToolArgs.value as string) ?? '{}',
        toolArgsRedactedPathsJson: JSON.stringify(
          redactedToolArgs.redactedPaths,
        ),
        agentStateJson: (redactedAgentState.value as string) ?? '{}',
        agentStateRedactedPathsJson: JSON.stringify(
          redactedAgentState.redactedPaths,
        ),
        friskToolId: registeredTool?.id,
        friskToolVersionId: registeredTool?.versionId,
        llmReasoning: llmReasoning ?? undefined,
      })

      const spanContext = trace.setSpan(context.active(), span.otelSpan)
      const traceContextCarrier: TraceContextCarrier = {}
      propagation.inject(spanContext, traceContextCarrier)

      try {
        const coreResult = this.frisk.decideToolCall({
          toolCall: {
            ...toolCall,
            args: removeLlmReasoningArg(toolCall.args),
          },
          agentState,
          traceContextCarrier,
        })

        const result: ToolCallDecision = {
          outcome: coreResult.outcome,
          rulesMatchedCount: coreResult.rulesMatchedCount,
          reason: coreResult.reason,
          policyId: coreResult.policyId,
          policyVersionId: coreResult.policyVersionId,
        }

        span.setAttributes({
          decisionOutcome: result.outcome,
          decisionMatchedRuleCount: Number(result.rulesMatchedCount),
          decisionReason: result.reason ?? undefined,
        })
        return result
      } catch (error) {
        const wrapped = new ToolCallEvaluationError(error as Error)
        captureError(wrapped, this.logger, {
          toolName: toolCall.name,
          toolCallId: toolCall.id,
        })

        span.setAttributes({
          errorType: wrapped.constructor.name,
          errorMessage: wrapped.message,
        })

        return {
          outcome: DecisionOutcome.ERROR,
          rulesMatchedCount: 0,
          reason: wrapped.message,
        }
      }
    } finally {
      span.end()
    }
  }

  private *createToolCallSequenceGenerator(): Generator<number> {
    while (true) {
      yield this.toolCallSequenceNumber++
    }
  }

  public getToolCallCount(): number {
    return this.toolCallSequenceNumber
  }

  public incrementToolCallErrors() {
    this.toolCallErrorCount += 1
  }

  public getToolCallErrorCount(): number {
    return this.toolCallErrorCount
  }

  /**
   * Add one LLM call's token usage to the session running totals and mirror the
   * cumulative values onto the root session span. Missing counts default to 0.
   */
  public recordTokenUsage({
    inputTokens = 0,
    outputTokens = 0,
    totalTokens = 0,
  }: {
    inputTokens?: number
    outputTokens?: number
    totalTokens?: number
  }): void {
    this.tokenUsageInput += inputTokens
    this.tokenUsageOutput += outputTokens
    this.tokenUsageTotal += totalTokens
    this._rootSpan?.setAttributes({
      inputTokens: this.tokenUsageInput,
      outputTokens: this.tokenUsageOutput,
      totalTokens: this.tokenUsageTotal,
    })
  }

  public getTokenUsage(): {
    inputTokens: number
    outputTokens: number
    totalTokens: number
  } {
    return {
      inputTokens: this.tokenUsageInput,
      outputTokens: this.tokenUsageOutput,
      totalTokens: this.tokenUsageTotal,
    }
  }

  /**
   * Initialize tracing for this session
   */
  initTracing({ runId, agentState }: InitTracingOptions): SessionSpan {
    this._rootRunId = runId
    this._isTracing = true
    this._agentState = agentState

    const span = new SessionSpan({ tracer: this.tracer })
    span.setAttributes({ sessionId: this.id, remoteSessionId: runId })

    // Extract prompt from agent state if available using framework adapter
    const userPrompt = this.frisk.adapter.extractPrompt?.(agentState)
    if (userPrompt) {
      span.setAttributes({ prompt: userPrompt })
    }

    this.setRootSpan(span)
    return span
  }

  /**
   * End tracing for this session
   */
  endTracing(): void {
    if (this._rootSpan) {
      this._rootSpan.setAttributes({
        toolCallCount: this.getToolCallCount(),
        toolCallErrorCount: this.getToolCallErrorCount(),
        inputTokens: this.tokenUsageInput,
        outputTokens: this.tokenUsageOutput,
        totalTokens: this.tokenUsageTotal,
      })
      this._rootSpan.end()
    }
    this._isTracing = false

    // Unregister session from registry when tracing ends
    const registry = SessionRegistry.getInstance()
    registry.unregister(this.id)
  }

  /**
   * Set the root span for the session
   */
  setRootSpan(span: SessionSpan): void {
    this._rootSpan = span
  }

  private static generateSessionId() {
    return uuidv4()
  }
}
